import React from 'react';
import { Helmet } from 'react-helmet-async';

const Payments = () => {
  return (
    <>
      <Helmet>
        <title>Payment Methods & Security - Dental Stores</title>
        <meta
          name="description"
          content="Learn about secure payment methods accepted at Dental Stores and our commitment to protecting your financial information."
        />
      </Helmet>
      <div style={{ padding: '48px 24px 100px', maxWidth: '980px', margin: '0 auto', color: '#1f2933' }}>
        <header style={{ marginBottom: '48px' }}>
          <p
            style={{
              margin: 0,
              color: '#1565C0',
              fontWeight: '600',
              letterSpacing: '0.3em',
              textTransform: 'uppercase',
              fontSize: '12px',
            }}
          >
            Secure checkout
          </p>
          <h1
            style={{
              fontSize: '38px',
              fontWeight: '700',
              margin: '12px 0 0',
              lineHeight: 1.25,
              color: '#102a43',
            }}
          >
            Payment Methods & Security
          </h1>
          <p style={{ marginTop: '18px', fontSize: '17px', lineHeight: 1.7, maxWidth: '720px', color: '#52606d' }}>
            Choose the way you want to pay—every option is protected by bank-grade security and monitored in real time.
          </p>
        </header>

        <section style={{ display: 'grid', gap: '36px' }}>
          <article style={{ display: 'grid', gap: '24px' }}>
            <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#102a43' }}>Accepted Payment Methods</h2>
            <div
              style={{
                display: 'grid',
                gap: '20px',
                gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              }}
            >
              {[
                {
                  title: 'Credit & Debit Cards',
                  description: 'Instant approvals on all major cards.',
                  items: ['Visa', 'MasterCard', 'American Express', 'RuPay'],
                  extra: 'Processing time: Instant for valid cards.',
                },
                {
                  title: 'Net Banking',
                  description: 'Pay directly from your bank with encrypted net banking.',
                  items: ['Supports SBI, HDFC, ICICI, Axis, and more', 'Real-time transaction updates'],
                },
                {
                  title: 'UPI Payments',
                  description: 'Quick mobile payments with no extra fees.',
                  items: ['Google Pay', 'PhonePe', 'Paytm', 'BHIM UPI', 'Amazon Pay'],
                },
                {
                  title: 'Cash on Delivery',
                  description: 'Pay when your order arrives.',
                  items: ['Available for orders above ₹500 and below ₹20,000', 'Cash or card accepted by delivery partner'],
                },
              ].map((method) => (
                <div
                  key={method.title}
                  style={{
                    border: '1px solid #d9e2ec',
                    borderRadius: '16px',
                    padding: '22px',
                    display: 'grid',
                    gap: '10px',
                    backgroundColor: '#f8fafc',
                  }}
                >
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#102a43' }}>{method.title}</h3>
                  <p style={{ margin: 0, fontSize: '15px', color: '#52606d' }}>{method.description}</p>
                  <ul style={{ margin: 0, paddingLeft: '18px', display: 'grid', gap: '6px', fontSize: '15px' }}>
                    {method.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                  {method.extra && (
                    <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#334e68' }}>
                      <strong>{method.extra}</strong>
                    </p>
                  )}
                </div>
              ))}
            </div>
          </article>

          <article style={{ display: 'grid', gap: '16px' }}>
            <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#102a43' }}>Your Security Comes First</h2>
            <div style={{ display: 'grid', gap: '16px', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
              {[
                {
                  title: '256-bit SSL Encryption',
                  copy: 'All data is encrypted during transmission—the same protection used by global banks.',
                },
                {
                  title: 'PCI DSS Compliant',
                  copy: 'We meet strict Payment Card Industry standards to keep card transactions safe.',
                },
                {
                  title: 'Tokenized Payments',
                  copy: 'Card details are never stored; tokens handle future transactions securely.',
                },
                {
                  title: 'Fraud Monitoring',
                  copy: 'AI-driven tools monitor every payment to block suspicious activity instantly.',
                },
              ].map((item) => (
                <div
                  key={item.title}
                  style={{ border: '1px solid #d9e2ec', borderRadius: '16px', padding: '22px', backgroundColor: '#ffffff' }}
                >
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#102a43' }}>{item.title}</h3>
                  <p style={{ margin: '10px 0 0', fontSize: '15px', color: '#52606d', lineHeight: 1.6 }}>{item.copy}</p>
                </div>
              ))}
            </div>
          </article>

          <article style={{ display: 'grid', gap: '16px' }}>
            <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#102a43' }}>Payment Journey</h2>
            <ol style={{ margin: 0, paddingLeft: '22px', display: 'grid', gap: '12px', fontSize: '16px', color: '#1f2933' }}>
              <li>
                <strong>Order confirmation:</strong> Instant email with your order details and tracking link where available.
              </li>
              <li>
                <strong>Verification step:</strong> High-value or first-time orders may need a quick confirmation call for
                added security.
              </li>
              <li>
                <strong>Failed payment help:</strong> Double-check card details, funds, expiry date, or network before
                trying again—or contact us for support.
              </li>
            </ol>
          </article>

          <article style={{ display: 'grid', gap: '12px' }}>
            <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#102a43' }}>International & Corporate Orders</h2>
            <p style={{ margin: 0, fontSize: '17px', lineHeight: 1.7, color: '#52606d' }}>
              We accept international cards, wire transfers, and (for select orders) PayPal. For bulk or institutional
              purchases, our finance team can coordinate custom invoicing and payment terms.
            </p>
          </article>

          <article style={{ display: 'grid', gap: '12px' }}>
            <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#102a43' }}>Payment Terms</h2>
            <ul style={{ margin: 0, paddingLeft: '22px', display: 'grid', gap: '10px', fontSize: '16px', color: '#1f2933' }}>
              <li>
                <strong>Prepaid orders:</strong> Processed once payment is confirmed—no hidden fees added later.
              </li>
              <li>
                <strong>COD orders:</strong> Keep cash or card ready at delivery; delivery partners cannot wait beyond
                scheduled slots.
              </li>
              <li>
                <strong>Taxes & charges:</strong> All applicable taxes display during checkout for full transparency.
              </li>
            </ul>
          </article>

          <article
            style={{ backgroundColor: '#f8fafc', border: '1px solid #d9e2ec', borderRadius: '16px', padding: '24px', display: 'grid', gap: '10px' }}
          >
            <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '600', color: '#102a43' }}>Need payment support?</h2>
            <p style={{ margin: 0, fontSize: '16px', color: '#52606d' }}>Our finance specialists will help resolve any payment hiccup.</p>
            <div style={{ display: 'grid', gap: '6px', fontSize: '16px', color: '#1f2933' }}>
              <span>
                <strong>Phone:</strong> +91 70931 34343
              </span>
              <span>
                <strong>Email:</strong> payments@dentalstores.in
              </span>
              <span>
                <strong>Hours:</strong> Mon–Sat, 10:30 AM – 7:00 PM IST
              </span>
            </div>
          </article>

          <article style={{ display: 'grid', gap: '8px' }}>
            <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#102a43' }}>Security Guarantee</h2>
            <p style={{ margin: 0, fontSize: '17px', lineHeight: 1.7, color: '#52606d' }}>
              If you ever notice unauthorized activity on your account, let us know immediately. We'll freeze the order,
              investigate, and keep you updated at every step.
            </p>
          </article>
        </section>
      </div>
    </>
  );
};

export default Payments;
